import Phaser from 'phaser';
import { BULLET, BULLET_FX } from '../config/GameConfig';
import type { BulletBuffKind } from '../config/GameConfig';
import type Enemy from './Enemy';

/**
 * Bullet —— 玩家子弹
 *
 * 高级行为（由词条解锁，由 Player 在发射后写入这些字段）：
 *   - pierce    穿透次数。命中敌人时若 pierce>0 则不销毁、pierce--
 *   - homing    是否启用跟踪；为 true 时由 MainScene 每帧调 homingUpdate
 *   - ricochet  反弹次数。命中并 destroy 之前若 ricochet>0：
 *               不销毁，反过来朝最近敌人/向上重射，ricochet--
 *   - effect    元素 buff（fire / ice / chain），命中时走对应特殊效果
 */
export default class Bullet extends Phaser.GameObjects.Image {
  declare body: Phaser.Physics.Arcade.Body;
  damage: number;
  pierce: number;
  homing: boolean;
  ricochet: number;
  effect: BulletBuffKind | null = null;
  /** 本颗子弹已经命中过的敌人，穿透时避免同一敌人连续 overlap 被重复扣血 */
  hitEnemies: Set<Enemy>;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, 'bullet');
    scene.add.existing(this);
    scene.physics.add.existing(this);

    // 子弹头贴图是 12×22 矩形，但碰撞用居中的小圆（命中手感跟原来一致）
    // setCircle 默认 offset 是矩形左上角，要手动把圆心挪到贴图中心
    const r = BULLET.radius;
    const tw = BULLET.textureWidth;
    const th = BULLET.textureHeight;
    this.body.setCircle(r, tw / 2 - r, th / 2 - r);
    this.body.setAllowGravity(false);
    this.body.setVelocity(0, BULLET.speed);

    // 贴图默认弹头朝上（y 负方向），子弹默认向上飞，所以 0 弧度即可。
    // 跟踪弹的 homingUpdate 会按速度方向重新 setRotation。
    this.setRotation(0);

    this.damage = BULLET.damage;
    this.pierce = 0;
    this.homing = false;
    this.ricochet = 0;
    this.hitEnemies = new Set();
  }

  /**
   * 跟踪逻辑：每帧由 MainScene 调用
   * - 在最近 enemyGroup 中找一个最近敌人（在 homingRange 内）
   * - 把 velocity 朝目标方向旋转一点点（受 turnRate 限制）
   */
  homingUpdate(delta: number, enemyGroup: Phaser.Physics.Arcade.Group): void {
    if (!this.homing || !this.body) return;
    let nearest: Phaser.GameObjects.GameObject | null = null;
    let nearestD2 = BULLET_FX.homingRange * BULLET_FX.homingRange;
    enemyGroup.getChildren().forEach((e) => {
      const ent = e as unknown as { active: boolean; _dead?: boolean; x: number; y: number };
      if (!ent.active || ent._dead) return;
      // 只追屏内、且在子弹上方一点的敌人，避免子弹掉头往下飞
      if (ent.y > this.y - 8) return;
      const dx = ent.x - this.x;
      const dy = ent.y - this.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < nearestD2) {
        nearestD2 = d2;
        nearest = e;
      }
    });
    if (!nearest) return;

    const target = nearest as unknown as { x: number; y: number };
    const speed = Math.hypot(this.body.velocity.x, this.body.velocity.y) || Math.abs(BULLET.speed);
    const curAng = Math.atan2(this.body.velocity.y, this.body.velocity.x);
    const tgtAng = Math.atan2(target.y - this.y, target.x - this.x);
    let dAng = Phaser.Math.Angle.Wrap(tgtAng - curAng);
    const maxStep = (BULLET_FX.homingTurnRate * delta) / 1000;
    dAng = Phaser.Math.Clamp(dAng, -maxStep, maxStep);
    const newAng = curAng + dAng;
    this.body.setVelocity(Math.cos(newAng) * speed, Math.sin(newAng) * speed);
    this.setRotation(newAng + Math.PI / 2);
  }
}
